import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector as useReduxSelector } from "react-redux";
import environment from "web/script/modules/environment";
import storage from "web/script/utils/storage";
import { EU_COUNTRIES } from "web/react/constants";
import { useSmallBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import FollowDesignerButton from "web/react/components/follow-designer-button/follow-designer-button";
import styles from "./follow-sticky-banner.css";

const ONE_HOUR = 3600000;

function extractCookieBannerState(state) {
    return state.cookieBannerReducer.cookieIsAccepted;
}

function FollowStickyBanner({
    lystId,
    registrationReason,
    followedText,
    notFollowedText,
    isAuthenticated,
    isFollowing,
    description,
    profileImage,
    forceDisplay,
}) {
    const [show, setShow] = useState(false);
    let [closed, setClosed] = useState(false);
    const wrapperRef = useRef(null);

    const country = environment.get("country");
    const isEu = EU_COUNTRIES.has(country);

    // This makes the banner appear in the bottom of the screen
    const bottomCookieBanner = isEu && environment.getFeature("mg_cookie_banner_always_at_bottom");

    // Redux state to see if cookie is accepted.
    // Will cause re-render when its Accepted
    const cookieIsAccepted = useReduxSelector(extractCookieBannerState);

    // This is to check if to hide the banner when Follow button
    // in the top is visible
    const hideDUBanner =
        !useSmallBreakpoint() &&
        (environment.getFeature("mg_hide_du_banner_if_du_cta_in_view") ||
            environment.getFeature("mg_du_banner_ios_to_app"));

    // Check if banner should be visible above the cookie banner
    let aboveCookieBanner = !cookieIsAccepted && bottomCookieBanner;

    function displayBanner() {
        setShow(true);
        analytics.event("du_feed_banner", "banner_shown", "du_banner", true); // non interactive
        storage.remove("hideStickyBannerTimestamp");
    }

    const triggerBanner = useCallback((delay) => {
        let hideStickyBannerTimestamp = storage.get("hideStickyBannerTimestamp");
        let timer;
        if (
            !hideStickyBannerTimestamp ||
            hideStickyBannerTimestamp < new Date().getTime() - ONE_HOUR
        ) {
            timer = setTimeout(displayBanner, delay);
        }

        return () => {
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (closed || hideDUBanner) {
            return;
        }
        triggerBanner(5000);
    });

    useEffect(() => {
        if (closed || !hideDUBanner) {
            return;
        }

        if (forceDisplay) {
            triggerBanner(500);
        } else {
            setShow(false);
        }
    }, [forceDisplay, closed, triggerBanner, hideDUBanner]);

    function triggerClose() {
        setShow(false);
        setClosed(true);
        storage.set("hideStickyBannerTimestamp", new Date().getTime());
        analytics.event("du_feed_banner", "banner_hidden", "du_banner", true); // non interactive
    }
    const bannerClassNames = classnames(styles.stickyBanner, {
        [styles.stickyBannerShow]: show && !closed,
        [styles.aboveCookieBanner]: aboveCookieBanner,
    });

    let renderedProfileImage = "";
    if (profileImage) {
        renderedProfileImage = <img src={profileImage} className={styles.imageProfile} />;
    }
    return (
        <div ref={wrapperRef}>
            <div className={bannerClassNames}>
                <div className={styles.wrapper}>
                    <div>
                        {renderedProfileImage}
                        <div
                            className={styles.content}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>
                    <div>
                        <FollowDesignerButton
                            className={styles.button}
                            lystId={lystId}
                            registrationReason={registrationReason}
                            followedText={followedText}
                            notFollowedText={notFollowedText}
                            isAuthenticated={isAuthenticated}
                            isFollowing={isFollowing}
                            description={description}
                            analyticsParameters={[
                                "du_feed_banner",
                                "click_banner_cta",
                                "du_banner",
                            ]}
                        />
                        <button className={styles.closeButton} onClick={triggerClose}>
                            <SVGIcon name="cross" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FollowStickyBanner;

FollowStickyBanner.propTypes = {
    lystId: PropTypes.number,
    registrationReason: PropTypes.string,
    followedText: PropTypes.string,
    notFollowedText: PropTypes.string,
    isAuthenticated: PropTypes.string,
    isFollowing: PropTypes.string,
    description: PropTypes.string,
    profileImage: PropTypes.string,
    forceDisplay: PropTypes.bool,
};
